import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IAppRunContext } from './abstractions';
import { ITenantSettings } from './abstractions/ITenantSettings';
import { IUserAdminRoleSetting } from './abstractions/IUserAdminRoleSettings';

interface SettingsState {
  tenantSettings: ITenantSettings | null; // Stores tenant settings as a key-value pair
  appRunContext: IAppRunContext | null; // Stores the current run context
  userAdminSettings?: IUserAdminRoleSetting

}

const initialState: SettingsState = {
  tenantSettings: null, // Initial state is null
  appRunContext: null, // Initial state for appRunContext is null
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Action to add or update tenantSettings
    setTenantSettings(state, action: PayloadAction<ITenantSettings>) {
      state.tenantSettings = action.payload;
    },
    // Action to reset tenantSettings to null
    resetTenantSettings(state) {
      state.tenantSettings = null;
    },
    // Action to update appRunContext
    setAppRunContext(state, action: PayloadAction<IAppRunContext>) {
      state.appRunContext = { ...state.appRunContext, ...action.payload };
    },

    setUserAdminSettings(state, action: PayloadAction<IUserAdminRoleSetting>) {
      state.userAdminSettings = action.payload;
    },
    resetUserAdminSettings(state) {
      state.userAdminSettings = undefined;
    }
  },
  selectors: {
    // Selector to get tenantSettings
    tenantSettings: (state: SettingsState) => state.tenantSettings,

    // Selector to check if admin consent is provided
    isAdminConsentProvided: (state: SettingsState): boolean => {
      const tenantSettings = state.tenantSettings;
      return (
        tenantSettings?.developerToolsForShowingAppUsageMetrics.some(
          (tool) => tool.toLowerCase() === 'developerportal'
        ) ?? false
      );
    },
    // Selector to fetch appRunContext
    appRunContext: (state: SettingsState) => state.appRunContext,

    isCurrentUserAdmin: (state: SettingsState): boolean => {
      const userAdminSettings = state.userAdminSettings;
      return userAdminSettings?.isAdmin ?? false;
    }
  },
});

export const { setTenantSettings, resetTenantSettings } = settingsSlice.actions;

export default settingsSlice.reducer;
